import apiClient from './axios';
import type { ApiEnvelope } from '../types';

export type ProductStatus = 'active' | 'draft' | 'archived';
export type ProductAttributeInputType =
  | 'text'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'date';

export type ProductAttributes = Record<string, string | number | boolean | string[]>;

export interface ProductVariantPayload {
  id?: string;
  name: string;
  sku?: string | null;
  options?: ProductAttributes;
  price: number;
  regularPrice?: number | null;
  stockQty?: number | null;
  imageUrls?: string[];
  status?: ProductStatus;
  weightKg?: number;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  logisticsCategoryId?: string | null;
}

export interface VendorProductVariant extends ProductVariantPayload {
  id: string;
  productId: string;
  sku: string | null;
  options: ProductAttributes;
  imageUrls: string[];
  status: ProductStatus;
  stockQty: number | null;
  weightKg: number;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  logisticsCategoryId: string | null;
  sortOrder: number;
}

export interface ProductAttributeDefinition {
  id: string;
  categoryId: string;
  subCategoryId: string | null;
  key: string;
  label: string;
  inputType: ProductAttributeInputType;
  options: string[];
  required: boolean;
  aiHint: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface VariantOptionPreset {
  key: string;
  label: string;
  values: string[];
  source: 'admin' | 'vendor';
  categoryId: string;
  subCategoryId: string | null;
}

export interface VendorSubCategory {
  id: string;
  slug: string;
  name: string;
}

export interface VendorCategory {
  id: string;
  slug: string;
  name: string;
  imageUrl: string | null;
  subCategories: VendorSubCategory[];
}

export interface VendorProduct {
  id: string;
  vendorId: string;
  categoryId: string | null;
  subCategoryId: string | null;
  name: string;
  brand: string | null;
  description: string | null;
  attributes: ProductAttributes;
  price: number;
  regularPrice?: number | null;
  priceMin?: number;
  priceMax?: number;
  imageUrls: string[];
  featured: boolean;
  status: ProductStatus;
  stockQty: number | null;
  totalStockQty?: number | null;
  hasVariants?: boolean;
  variants?: VendorProductVariant[];
  weightKg?: number;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  logisticsCategoryId?: string | null;
  rating: number;
  ratingsCount: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProductListResponse {
  items: VendorProduct[];
  meta: { total: number; page: number; limit: number; totalPages: number };
}

export interface CreateProductPayload {
  name: string;
  price: number;
  regularPrice?: number | null;
  categoryId: string;
  subCategoryId?: string | null;
  brand?: string;
  description?: string;
  attributes?: ProductAttributes;
  imageUrls?: string[];
  stockQty?: number | null;
  weightKg?: number;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  logisticsCategoryId?: string | null;
  variants?: ProductVariantPayload[];
  status?: ProductStatus;
}

export type UpdateProductPayload = Partial<
  Omit<CreateProductPayload, 'featured'>
>;

export interface GenerateProductDescriptionPayload {
  name: string;
  brand?: string;
  categoryId?: string;
  categoryName?: string;
  subCategoryId?: string | null;
  subCategoryName?: string;
  price?: number;
  stockQty?: number | null;
  imageUrls?: string[];
  attributes?: ProductAttributes;
  clarificationAnswers?: ProductAttributes;
  variantOptions?: Record<string, string[]>;
}

export type GenerateProductDescriptionResponse =
  | { status: 'ready'; description: string }
  | {
      status: 'needs_more_info';
      questions: Array<{
        key: string;
        label: string;
        question: string;
        inputType: ProductAttributeInputType;
        options?: string[];
      }>;
    };

export interface ProductImportItem {
  id: string;
  jobId: string;
  rowNumber: number;
  sourceProductId: string | null;
  normalized: CreateProductPayload;
  status: 'ready' | 'warning' | 'error' | 'committed';
  errors: string[];
  warnings: string[];
  createdProductId: string | null;
  createdVariantIds: string[];
}

export interface ProductImportJob {
  id: string;
  vendorId: string;
  source: 'csv' | 'shopify' | 'woocommerce';
  status: 'preview' | 'committed' | 'failed';
  sourceMetadata: Record<string, unknown>;
  totalItems: number;
  readyItems: number;
  warningItems: number;
  errorItems: number;
  committedItems: number;
  committedAt: string | null;
  createdAt: string;
  updatedAt: string;
  items: ProductImportItem[];
}

export const productsApi = {
  list: async (opts: {
    status?: ProductStatus;
    q?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ProductListResponse> => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const { data } = await apiClient.get<ApiEnvelope<ProductListResponse>>(
      `/vendor/products${params.toString() ? `?${params.toString()}` : ''}`,
    );
    return data.data;
  },

  getOne: async (id: string): Promise<VendorProduct> => {
    const { data } = await apiClient.get<ApiEnvelope<VendorProduct>>(
      `/vendor/products/${id}`,
    );
    return data.data;
  },

  create: async (payload: CreateProductPayload): Promise<VendorProduct> => {
    const { data } = await apiClient.post<ApiEnvelope<VendorProduct>>(
      '/vendor/products',
      payload,
    );
    return data.data;
  },

  update: async (
    id: string,
    payload: UpdateProductPayload,
  ): Promise<VendorProduct> => {
    const { data } = await apiClient.patch<ApiEnvelope<VendorProduct>>(
      `/vendor/products/${id}`,
      payload,
    );
    return data.data;
  },

  archive: async (id: string): Promise<VendorProduct> => {
    const { data } = await apiClient.delete<ApiEnvelope<VendorProduct>>(
      `/vendor/products/${id}`,
    );
    return data.data;
  },

  generateDescription: async (
    payload: GenerateProductDescriptionPayload,
  ): Promise<GenerateProductDescriptionResponse> => {
    const { data } =
      await apiClient.post<ApiEnvelope<GenerateProductDescriptionResponse>>(
        '/vendor/products/generate-description',
        payload,
      );
    return data.data;
  },

  listAttributeDefinitions: async (opts: {
    categoryId: string;
    subCategoryId?: string | null;
  }): Promise<ProductAttributeDefinition[]> => {
    const params = new URLSearchParams({ categoryId: opts.categoryId });
    if (opts.subCategoryId) params.set('subCategoryId', opts.subCategoryId);
    const { data } = await apiClient.get<
      ApiEnvelope<ProductAttributeDefinition[]>
    >(`/vendor/products/attribute-definitions?${params.toString()}`);
    return data.data;
  },

  listVariantOptionPresets: async (opts: {
    categoryId: string;
    subCategoryId?: string | null;
  }): Promise<VariantOptionPreset[]> => {
    const params = new URLSearchParams({ categoryId: opts.categoryId });
    if (opts.subCategoryId) params.set('subCategoryId', opts.subCategoryId);
    const { data } = await apiClient.get<ApiEnvelope<VariantOptionPreset[]>>(
      `/vendor/products/variant-option-presets?${params.toString()}`,
    );
    return data.data;
  },

  /** Active categories + subcategories for the product-form selectors. */
  listCategories: async (): Promise<VendorCategory[]> => {
    const { data } = await apiClient.get<ApiEnvelope<VendorCategory[]>>(
      '/vendor/products/categories',
    );
    return data.data;
  },

  downloadCsvTemplate: async (): Promise<Blob> => {
    const { data } = await apiClient.get('/vendor/product-imports/csv-template', {
      responseType: 'blob',
    });
    return data;
  },

  previewCsv: async (payload: {
    csvText: string;
    fileName?: string;
  }): Promise<ProductImportJob> => {
    const { data } = await apiClient.post<ApiEnvelope<ProductImportJob>>(
      '/vendor/product-imports/csv/preview',
      payload,
    );
    return data.data;
  },

  previewShopify: async (payload: {
    shopDomain: string;
    accessToken: string;
    apiVersion?: string;
  }): Promise<ProductImportJob> => {
    const { data } = await apiClient.post<ApiEnvelope<ProductImportJob>>(
      '/vendor/product-imports/shopify/preview',
      payload,
    );
    return data.data;
  },

  createShopifyConnectUrl: async (payload: {
    shopDomain: string;
  }): Promise<{ authUrl: string }> => {
    const { data } = await apiClient.post<ApiEnvelope<{ authUrl: string }>>(
      '/vendor/product-imports/shopify/connect-url',
      payload,
    );
    return data.data;
  },

  previewWooCommerce: async (payload: {
    storeUrl: string;
    consumerKey: string;
    consumerSecret: string;
  }): Promise<ProductImportJob> => {
    const { data } = await apiClient.post<ApiEnvelope<ProductImportJob>>(
      '/vendor/product-imports/woocommerce/preview',
      payload,
    );
    return data.data;
  },

  getImportJob: async (jobId: string): Promise<ProductImportJob> => {
    const { data } = await apiClient.get<ApiEnvelope<ProductImportJob>>(
      `/vendor/product-imports/${jobId}`,
    );
    return data.data;
  },

  updateImportItem: async (
    jobId: string,
    itemId: string,
    normalized: CreateProductPayload,
  ): Promise<ProductImportJob> => {
    const { data } = await apiClient.patch<ApiEnvelope<ProductImportJob>>(
      `/vendor/product-imports/${jobId}/items/${itemId}`,
      { normalized },
    );
    return data.data;
  },

  commitImport: async (
    jobId: string,
    itemIds?: string[],
  ): Promise<ProductImportJob> => {
    const { data } = await apiClient.post<ApiEnvelope<ProductImportJob>>(
      `/vendor/product-imports/${jobId}/commit`,
      itemIds ? { itemIds } : {},
    );
    return data.data;
  },
};
